import { ActionCard } from '@playbooks/core/src/interfaces/ActionCard'

import { getWebPlaysDbLazy, ACTION_CARDS_STORE_NAME, ACTION_CARDS_STORE_INDEX_BY_PLAYBOOKS } from './dbService'

async function getStore(this: ActionCardsDbStore, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
  if (!this.dbPromise) {
    this.dbPromise = getWebPlaysDbLazy()
  }

  const db = await this.dbPromise
  return db.transaction(ACTION_CARDS_STORE_NAME, mode).objectStore(ACTION_CARDS_STORE_NAME)
}

async function remove(store: IDBObjectStore, key: IDBValidKey) {
  const request = store.delete(key)
  return new Promise<void>((resolve, reject) => {
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

function getStorageIndexKey(storageId: string | number | null): IDBValidKey {
  if (storageId === null) return 0

  // Preserve the existing numeric index while allowing source-qualified storage identities.
  return typeof storageId === 'string' && !/^\d+$/.test(storageId) ? storageId : Number(storageId)
}

export class ActionCardsDbStore {
  dbPromise: Promise<IDBDatabase> | null = null

  async get(key: string): Promise<ActionCard> {
    const store = await getStore.call(this, 'readwrite')
    const request = store.get(key)

    return new Promise<ActionCard>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result?.actionCardData)
      request.onerror = () => reject(request.error)
    })
  }

  async save(key: string, storageId: string | number | null, actionCardData: ActionCard) {
    const store = await getStore.call(this, 'readwrite')

    const storageIndexKey = getStorageIndexKey(storageId)
    // make deep copy of action card data to exclude non clonable items like functions, etc.
    // Keep the legacy `playbookId` property because the existing IndexedDB index uses that path.
    const record = { key, playbookId: storageIndexKey, actionCardData: JSON.parse(JSON.stringify(actionCardData)) }

    const request = store.put(record)
    return new Promise<void>((resolve, reject) => {
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async removeByStorageId(storageId: string | number) {
    const storageIndexKey = getStorageIndexKey(storageId)
    const store = await getStore.call(this, 'readwrite')
    const request = store.index(ACTION_CARDS_STORE_INDEX_BY_PLAYBOOKS).getAllKeys(storageIndexKey)

    return new Promise<void>((resolve, reject) => {
      request.onsuccess = async () => {
        const keys = request.result
        if (keys && keys.length > 0) {
          const removePromises = keys.map(key => remove(store, key))
          await Promise.all(removePromises)
          resolve()
        } else {
          resolve()
        }
      }

      request.onerror = () => reject(request.error)
    })
  }
}
